import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './MailboxPassword.css';

const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'https://trading.omniscalping.com/api';

function authHeaders() {
  const token = localStorage.getItem('sessionToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function MailboxPassword({ onBack, initialEmail = '', embedded = false }) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState(null);
  const [linkedMailbox, setLinkedMailbox] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/mail/config`).then((r) => setConfig(r.data.config)).catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;
    axios
      .get(`${API_BASE}/mail/my-mailbox`, { headers: authHeaders() })
      .then((r) => {
        if (r.data.mailbox?.email) {
          setLinkedMailbox(r.data.mailbox);
          if (!initialEmail) setEmail(r.data.mailbox.email);
        }
      })
      .catch(() => {});
  }, [initialEmail]);

  const domain = config?.domain || 'omniscalping.com';
  const host = config?.host || `mail.${domain}`;

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_BASE}/mail/password/request`, { email: email.trim().toLowerCase() });
      if (res.data?.devVerificationCode) {
        setCode(res.data.devVerificationCode);
      }
      setSuccess(res.data.message || 'Check your inbox for a verification code.');
      setStep('reset');
    } catch (err) {
      setError(
        [err.response?.data?.error, err.response?.data?.details].filter(Boolean).join(' — ') ||
          err.message ||
          'Request failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_BASE}/mail/password/reset`, {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        newPassword,
      });
      setSuccess(res.data.message || 'Password updated.');
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const wrapperClass = embedded ? 'mailbox-password-embedded' : 'mailbox-password-page';

  return (
    <div className={wrapperClass}>
      <div className="mailbox-password-card">
        {!embedded && onBack && (
          <button type="button" className="mailbox-back-btn" onClick={onBack}>
            ← Back
          </button>
        )}

        <h2>📧 Mailbox password</h2>
        <p className="mailbox-subtitle">
          Reset your <strong>@{domain}</strong> email password. A code is sent to that mailbox.
        </p>

        {linkedMailbox && (
          <div className="mailbox-linked-banner">
            Your linked mailbox: <strong>{linkedMailbox.email}</strong>
          </div>
        )}

        <div className="mailbox-settings-hint">
          <div><strong>IMAP</strong> {host}:993 (SSL)</div>
          <div><strong>SMTP</strong> {host}:587 (STARTTLS)</div>
        </div>

        {error && <div className="mailbox-error">{error}</div>}
        {success && step !== 'done' && <div className="mailbox-success">{success}</div>}

        {step === 'request' && (
          <form onSubmit={handleRequest}>
            <label className="mailbox-field">
              <span>Mailbox address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={`you@${domain}`}
                required
              />
            </label>
            <button type="submit" className="mailbox-btn mailbox-btn-primary" disabled={loading}>
              {loading ? 'Sending…' : 'Send verification code'}
            </button>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleReset}>
            <label className="mailbox-field">
              <span>Mailbox</span>
              <input type="email" value={email} readOnly />
            </label>
            <label className="mailbox-field">
              <span>Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code"
                required
              />
            </label>
            <label className="mailbox-field">
              <span>New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <label className="mailbox-field">
              <span>Confirm password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <div className="mailbox-btn-row">
              <button type="button" className="mailbox-btn" onClick={() => setStep('request')} disabled={loading}>
                Resend code
              </button>
              <button type="submit" className="mailbox-btn mailbox-btn-primary" disabled={loading}>
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div className="mailbox-done">
            <p>{success}</p>
            <p>Update Mail, Outlook, or your phone with the new password.</p>
            {onBack && (
              <button type="button" className="mailbox-btn mailbox-btn-primary" onClick={onBack}>
                Done
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
